import {
    CRS,
    type LatLng,
    type LatLngBounds,
    type LatLngExpression,
} from "leaflet";
import PropTypes from "prop-types";
import {
    type PropsWithChildren,
    type ReactNode,
    type Ref,
    use,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
} from "react";
import {
    FeatureGroup,
    MapContainer,
    useMap,
    useMapEvents,
} from "react-leaflet";

import type { LightGroupGeometryModel } from "@/types/entity-types";
import { Map as MapUtils } from "@/utils";
import PrintContext from "@/utils/print-context";

import BoxSelector from "./box-selector";
import GridLayer from "./grid-layer";
import Legend from "./legend";
import Light from "./light";
import {
    DrawLightGroup,
    EditLightGroup,
    LightGroupGeometry,
} from "./light-group";
import Marker from "./marker/Marker";
import TransformImage from "./transform-image";

export const FEETINMETER = 3.28084;

interface MapProps {
    onMapClick?: (latlng: LatLng) => void;
    children?: ReactNode;
    mapSettings: any;
    showGrid?: boolean;
    showBackground?: boolean;
    viewport?: any;
    highlight?: unknown;
    onUpdateViewport?: (viewport: {
        center: LatLngExpression;
        zoom: number;
        bounds: LatLngBounds;
    }) => void;
    mode: {
        name: string;
        props?: Record<string, any>;
    };
    groupGeometries?: LightGroupGeometryModel[];
    editingGroupGeometry?: LightGroupGeometryModel | null;
    selectedTool?: {
        name: string;
    };
    ref?: Ref<unknown>;
}

/**
 * Leaflet map surface for the floor plan, its lights, markers, and group geometry.
 *
 * @param root0 - The component props. `ref` exposes the Leaflet `map` instance.
 * @returns The rendered map.
 */
const Map = ({
    onMapClick,
    children,
    mapSettings,
    showGrid,
    showBackground,
    viewport,
    highlight,
    onUpdateViewport,
    mode,
    groupGeometries = [],
    editingGroupGeometry,
    selectedTool,
    ref,
}: MapProps) => {
    const childrenElementGroupRef = useRef(undefined);
    const printContainerRef = useRef(undefined);
    const printContext = use(PrintContext);

    // Register map container with print context for ReactToPrint
    useEffect(() => {
        if (printContainerRef.current && printContext?.setContext) {
            printContext.setContext(printContainerRef.current);
        }
    }, [printContext]);

    const visibleGroupGeometries = useMemo(
        () => groupGeometries.filter((g) => g.id !== editingGroupGeometry?.id),
        [groupGeometries, editingGroupGeometry],
    );

    const shouldDisplayGeometries = useMemo(() => {
        const selectingGroup = selectedTool?.name === "group";
        const editingGroup =
            mode.name === "group-edit" || mode.name === "group";
        return !!((selectingGroup || editingGroup) && groupGeometries);
    }, [selectedTool, mode, groupGeometries]);

    return (
        <div
            ref={printContainerRef}
            style={{
                position: "absolute",
                top: 0,
                left: 0,
                bottom: 0,
                right: 0,
            }}
        >
            <MapContainer
                preferCanvas
                crs={CRS.Simple}
                doubleClickZoom
                touchZoom
                zoomControl={false}
                maxZoom={20}
                zoomSnap={0.25}
                center={viewport?.center || [0, 0]}
                zoom={viewport?.zoom || 3}
                style={{
                    top: 0,
                    left: 0,
                    bottom: 0,
                    right: 0,
                    overflow: "hidden",
                    position: "absolute",
                    backgroundColor: "#fff",
                }}
            >
                <GridCenterHandler mapSettings={mapSettings} />
                <ViewportHandler onUpdateViewport={onUpdateViewport} />
                {onMapClick && <ClickHandler onMapClick={onMapClick} />}
                <MapRefHandler ref={ref}>
                    {showBackground && mapSettings.background && (
                        <TransformImage
                            url={mapSettings.background.image}
                            bounds={mapSettings.background.bounds}
                            options={{
                                swapXY: mapSettings.spaceDetails.swapXY,
                                negateX: mapSettings.spaceDetails.negateX,
                                negateY: mapSettings.spaceDetails.negateY,
                            }}
                        />
                    )}
                    <FeatureGroup ref={childrenElementGroupRef}>
                        {children}
                    </FeatureGroup>
                    <GridLayer
                        mapSettings={mapSettings}
                        showInternalLines={showGrid}
                        highlight={highlight}
                    />
                    {mode.name === "group" && (
                        <DrawLightGroup group={mode.props.group} />
                    )}
                    {mode.name === "group-edit" && (
                        <EditLightGroup geometry={editingGroupGeometry} />
                    )}
                    {shouldDisplayGeometries &&
                        visibleGroupGeometries.map((group) => (
                            <LightGroupGeometry key={group.id} {...group} />
                        ))}
                </MapRefHandler>
            </MapContainer>
        </div>
    );
};

Map.Legend = Legend;
Map.Light = Light;
Map.Marker = Marker;
Map.BoxSelector = BoxSelector;

Map.propTypes = {
    onMapClick: PropTypes.func,
    children: PropTypes.node,
    mapSettings: PropTypes.any,
    showGrid: PropTypes.bool,
    showBackground: PropTypes.bool,
    viewport: PropTypes.shape({
        zoom: PropTypes.number,
        center: PropTypes.array,
    }),
    highlight: PropTypes.any,
    onUpdateViewport: PropTypes.func,
    mode: PropTypes.object.isRequired,
    groupGeometries: PropTypes.array,
    editingGroupGeometry: PropTypes.object,
    selectedTool: PropTypes.object,
};

const getGridCenter = (mapSettings): LatLngExpression => {
    if (!mapSettings?.ceilingDetails || !mapSettings?.spaceDetails) {
        return [0, 0];
    }

    const { ceilingDetails, spaceDetails } = mapSettings;
    const gridOffsetX = ceilingDetails.gridOffSetX / FEETINMETER;
    const gridOffsetY = ceilingDetails.gridOffSetY / FEETINMETER;
    const floorHeight = MapUtils.Converter.convertFeetToMeters(
        spaceDetails.height,
    );

    const floorWidth = MapUtils.Converter.convertFeetToMeters(
        spaceDetails.width,
    );

    // Grid bounds: [gridOffsetY, gridOffsetX] to [floorHeight + gridOffsetY, floorWidth + gridOffsetX]
    // Center is midpoint of bounds
    return [gridOffsetY + floorHeight / 2, gridOffsetX + floorWidth / 2];
};

// Helper component to capture the map instance and expose it via ref
const MapRefHandler = ({
    children,
    ref,
}: PropsWithChildren<{ ref?: Ref<unknown> }>) => {
    const map = useMap();
    useImperativeHandle(ref, () => ({ map }), [map]);
    return <>{children}</>;
};

MapRefHandler.propTypes = {
    children: PropTypes.node,
};

// Component to center map on grid once on mount
const GridCenterHandler = ({ mapSettings }) => {
    const map = useMap();
    const hasCenteredRef = useRef(false);

    useEffect(() => {
        if (hasCenteredRef.current) return;
        if (!mapSettings?.ceilingDetails || !mapSettings?.spaceDetails) return;

        const center = getGridCenter(mapSettings);
        map.setView(center, map.getZoom());
        hasCenteredRef.current = true;
    }, [map, mapSettings]);

    return null;
};

GridCenterHandler.propTypes = {
    mapSettings: PropTypes.any,
};

// Component to handle viewport change events
const ViewportHandler = ({ onUpdateViewport }) => {
    const timeoutRef = useRef(null);

    useMapEvents({
        moveend: (e) => {
            if (!onUpdateViewport) return;
            // Throttle updates using a timeout
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
            timeoutRef.current = setTimeout(() => {
                const map = e.target;
                onUpdateViewport({
                    center: [map.getCenter().lat, map.getCenter().lng],
                    zoom: map.getZoom(),
                    bounds: map.getBounds(),
                });
            }, 100);
        },
        zoomend: (e) => {
            if (!onUpdateViewport) return;
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
            timeoutRef.current = setTimeout(() => {
                const map = e.target;
                onUpdateViewport({
                    center: [map.getCenter().lat, map.getCenter().lng],
                    zoom: map.getZoom(),
                    bounds: map.getBounds(),
                });
            }, 100);
        },
    });

    return null;
};

ViewportHandler.propTypes = {
    onUpdateViewport: PropTypes.func,
};

// Component to handle map click events
const ClickHandler = ({ onMapClick }) => {
    useMapEvents({
        click: (e) => {
            if (onMapClick) {
                onMapClick(e.latlng);
            }
        },
    });
    return null;
};

ClickHandler.propTypes = {
    onMapClick: PropTypes.func,
};

export default Map;
